"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { FISHER_EXACT_TEST_CALCULATOR_STRINGS } from "@/components/tools/fisher-exact-test-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { t } from "@/lib/i18n/interpolate";
import { logChoose } from "@/lib/stats/special";
import type { FisherResult } from "@/lib/tool-kit/stats-tests/fisher-exact";
import {
  type Alternative,
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";

/** Results for the Fisher's exact test calculator. */

type Strings = typeof FISHER_EXACT_TEST_CALCULATOR_STRINGS;

const MAX_TABLES = 40;
const MAX_TERMS = 8;
const RELATIVE_TOLERANCE = 1 + 1e-7;

/** C(n, k) as a whole number while it fits exactly in a double; null beyond. */
function choose(n: number, k: number): number | null {
  const value = Math.round(Math.exp(logChoose(n, k)));
  return value < 2 ** 53 ? value : null;
}

function pFor(r: FisherResult, alternative: Alternative): number {
  if (alternative === "less") {
    return r.pLess;
  }
  if (alternative === "greater") {
    return r.pGreater;
  }
  return r.pTwoSided;
}

function TablesList({
  r,
  strings,
  fmt,
}: {
  r: FisherResult;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  if (r.distribution.length > MAX_TABLES) {
    return <p className={HINT}>{strings.distributionHidden}</p>;
  }
  const a = r.table[0];
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.distributionHeading}</h3>
      <p className={HINT}>{strings.distributionHint}</p>
      <ResultTable
        head={[
          strings.aColumn,
          strings.probabilityColumn,
          strings.includedColumn,
        ]}
        rows={r.distribution.map(({ k, p }) => ({
          key: `k-${k}`,
          cells: [
            k === a ? t(strings.observedMark, { value: fmt(k) }) : fmt(k),
            formatP(p, fmt),
            p <= r.pObserved * RELATIVE_TOLERANCE ? strings.yes : strings.no,
          ],
        }))}
      />
    </>
  );
}

function FisherWorking({
  r,
  strings,
  fmt,
}: {
  r: FisherResult;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [a, b, c, d] = r.table;
  const n = (v: number) => formatStat(v, fmt);
  const [c1, c2, c3] = [choose(a + c, a), choose(b + d, b), choose(r.n, a + b)];
  const product =
    c1 !== null && c2 !== null && c3 !== null
      ? ` = ${fmt(c1)} × ${fmt(c2)} / ${fmt(c3)}`
      : "";
  const included = r.distribution.filter(
    ({ p }) => p <= r.pObserved * RELATIVE_TOLERANCE
  );
  const terms =
    included.length <= MAX_TERMS
      ? `${included.map(({ p }) => formatP(p, fmt)).join(" + ")} = `
      : "";
  return (
    <Working heading={strings.howCalculated}>
      <p>
        a + b = {fmt(a + b)}, c + d = {fmt(c + d)}, a + c = {fmt(a + c)}, b + d
        = {fmt(b + d)}, N = {fmt(r.n)}
      </p>
      <p>
        P(a) = C(a + c, a) × C(b + d, b) / C(N, a + b) = C({fmt(a + c)},{" "}
        {fmt(a)}) × C(
        {fmt(b + d)}, {fmt(b)}) / C({fmt(r.n)}, {fmt(a + b)}){product} ={" "}
        {formatP(r.pObserved, fmt)}
      </p>
      <p>
        {t(strings.workingTwoSided, {
          sum: `${terms}${formatP(r.pTwoSided, fmt)}`,
        })}
      </p>
      <p>
        OR = ad / bc = ({fmt(a)} × {fmt(d)}) / ({fmt(b)} × {fmt(c)}) ={" "}
        {n(r.sampleOddsRatio)}
      </p>
    </Working>
  );
}

export function FisherResults({
  r,
  alternative,
  alpha,
  strings,
  fmt,
}: {
  r: FisherResult;
  alternative: Alternative;
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => (Number.isNaN(v) ? "—" : formatStat(v, fmt));
  const p = pFor(r, alternative);
  const level = fmt(r.level, { style: "percent", maximumFractionDigits: 1 });
  const labels: Record<Alternative, string> = {
    "two-sided": strings.pTwoSided,
    less: strings.pLess,
    greater: strings.pGreater,
  };
  const apa = richTemplate(strings.apaTemplate, {
    p: apaP(p, fmt),
    or: fixed(r.conditionalMle, 2, fmt),
    level,
    low: fixed(r.ciLow, 2, fmt),
    high: fixed(r.ciHigh, 2, fmt),
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={labels[alternative]}
          value={formatP(p, fmt)}
        />
        <StatCard label={strings.sampleOr} value={n(r.sampleOddsRatio)} />
        <StatCard label={strings.conditionalOr} value={n(r.conditionalMle)} />
        <StatCard
          label={t(strings.ci, { level })}
          value={`[${n(r.ciLow)}, ${n(r.ciHigh)}]`}
        />
        <StatCard label={strings.pObserved} value={formatP(r.pObserved, fmt)} />
        <StatCard label={strings.n} value={fmt(r.n)} />
      </StatGrid>
      <Decision reject={p < alpha}>
        {t(p < alpha ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(p, fmt),
        })}
      </Decision>
      <p className={HINT}>{strings.orNote}</p>
      <ResultTable
        head={[
          strings.alternativeColumn,
          strings.exactColumn,
          strings.apaColumn,
        ]}
        rows={(["two-sided", "less", "greater"] as const).map((key) => ({
          key,
          cells: [
            labels[key],
            formatP(pFor(r, key), fmt),
            `p ${apaP(pFor(r, key), fmt)}`,
          ],
        }))}
      />
      <TablesList fmt={fmt} r={r} strings={strings} />
      <FisherWorking fmt={fmt} r={r} strings={strings} />
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}
